import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Building2, User, ArrowLeft, Pencil, MessageSquareText, Check } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { getUsersArr, getThreadTypesArr } from '../../Store/Reducers';
import {
  getThreadTypeMeta,
  getTaskColor,
  getColorClass,
  getThreadNameFromTypeID,
  currentUserHasPermission,
  getProductTypeByName,
  playOrderSound,
  getCurrentUser
} from '../../Utils/Utilities';
import { getTableDateOrWeekday } from '../../Utils/DateTime';
import { useThreadByID } from '../../Hooks/useThreadsData';
import { setGlobalLoading } from '../../Store/Actions';
import {
  performThreadActionAPI,
  updateThreadStageAPI,
  reassignThreadAPI,
  manageThreadAPI
} from '../Threads/ThreadsAPI';
import apiService from '../../Services/APIService';

// Import local components
import ThrDetailsInfo from './ThrDetails/ThrDetailsInfo';
import ThrDetailsStatus from './ThrDetails/ThrDetailsStatus';
import ThrDetailsActions from './ThrDetails/ThrDetailsActions';
import FeedbackSection from './ThreadDetails/FeedbackSection';
import ReassignDialog from '../Threads/ReassignDialog';
import CancelThreadDialog from '../../Components/Common/CancelThreadDialog';
import InquiryCloseDialog from '../../Components/Common/InquiryCloseDialog';
import ThreadEntry from '../Threads/ThreadEntry';
import MastersDialog from '../Settings/Masters';
import { useCurrency } from '../../Hooks/useCurrency';
import { cn } from '../../Lib/Cn';
import useIsMobile from '../../Hooks/useIsMobile';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose
} from '../../Components/UI/dialog'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from '../../Components/UI/alert-dialog';
import { Button } from '../../Components/UI/button';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '../../Components/UI/tooltip';
import { BsWhatsapp } from 'react-icons/bs';


const ThrDetails = ({
  isOpen,
  onClose = () => { },
  threadID = 0,
  initialData = null,
  isHistoryview = false,
  onThreadUpdated = () => { }
}) => {
  const [currentThreadData, setCurrentThreadData] = useState(null);
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const currencySymbol = useCurrency();

  const users = useSelector(getUsersArr);
  const threadTypesData = useSelector(getThreadTypesArr);
  const threadStages = useSelector(state => state.masterData.threadStages);
  const masterData = useSelector(state => state.masterData);

  const newThreadData = useThreadByID(threadID);

  // Dialog states
  const [dialogStates, setDialogStates] = useState({
    reassign: false,
    confirmDialog: false,
    rejectionDialog: false,
    inquiryClose: false,
    convertToSales: false,
    thrEntryCallFrom: ''
  });

  // Masters dialog state
  const [mastersDialog, setMastersDialog] = useState({
    open: false,
    masterType: '',
    directAdd: false,
    openEntry: false,
    showThreadTypeSelection: false,
    currentThreadTypeID: null
  });

  const [attachDocDialog, setAttachDocDialog] = useState(false);
  const attachFileInputRef = useRef(null);

  const threadTypeMeta = getThreadTypeMeta(currentThreadData?.typeID, threadTypesData);
  const canShowReassignButton = users.length > 1;

  // Check if user can edit threads (ThrActions 1 or 2)
  const canEditThread = currentUserHasPermission('ThrActions');
  const canShowEditButton = canEditThread <= 2 && canEditThread > 0;

  const rejectionReasonOptions = useMemo(() => {
    if (!currentThreadData || !masterData?.rejectionReasons) return [];
    const currentThreadType = currentThreadData.typeID;
    const threadReasons = masterData.rejectionReasons.find(item => item.TID === currentThreadType);
    if (!threadReasons) return [];
    try {
      const reasons = JSON.parse(threadReasons.Rsns);
      return reasons.map(reason => ({ value: reason.RID, label: reason.R, }));
    } catch (error) {
      console.error("Error parsing failure reasons:", error);
      return [];
    }
  }, [currentThreadData, masterData?.rejectionReasons]);

  const getExecutiveName = (pocUserID) => {
    const user = users.find((user) => user.userID === pocUserID);
    return user ? user.name : "--";
  };

  const getStageInfo = (typeID, stageID) => {
    const thread = threadStages.find(thread => thread.TID === typeID);
    if (!thread) return null;
    const stage = thread.Stages.find(stage => stage.SID === stageID);
    if (!stage) return null;
    return { SID: stage.SID, name: stage.S };
  };

  const getTransformedThreadData = (item) => {
    if (!item) return null;
    return {
      threadID: item.ThreadID || item.threadID,
      typeID: item.TypeID || item.typeID,
      agencyID: item.AgencyID || item.agencyID,
      connID: item.ConnID || item.connID,
      mainProduct: item.MainProduct || item.mainProduct || item.product || item.Product,
      mainQty: item.MainQty || item.mainQty || item.quantity || item.Quantity,
      model: item.Model || item.model,
      pocUserID: item.POCUserID || item.pocUserID,
      pocUserName: getExecutiveName(item.POCUserID || item.pocUserID),
      statusID: item.StatusID !== undefined ? item.StatusID : (item.statusID !== undefined ? item.statusID : (item.Sts !== undefined ? item.Sts : item.sts)),
      dueDT: item.DueDT || item.dueDT,
      dueDTF: getTableDateOrWeekday(item.DueDT || item.dueDT),
      dueDTColor: getColorClass(getTaskColor(item.DueDT || item.dueDT)),
      sinceDT: item.SinceDT || item.sinceDT,
      sinceDTF: getTableDateOrWeekday(item.SinceDT || item.sinceDT),
      reference: item.Reference || item.reference,
      bizProspect: item.BizProspect !== undefined ? item.BizProspect : item.bizProspect,
      currStageID: item.CurrStageID || item.currStageID,
      currStage: item.CurrStage || item.currStage || item.Status || item.status,
      status: item.Status || item.status,
      stageInfo: getStageInfo(item.TypeID || item.typeID, item.CurrStageID || item.currStageID),
      notes: item.Notes || item.notes,
      thrFile: item.ThrFile || item.thrFile || item.thrfile,
      source: item.Source || item.source,
      connectionData: {
        bizName: item.connectionData?.BizName || item.connectionData?.bizName || item.bizName || item.BizName,
        contactName: item.connectionData?.ContactName || item.connectionData?.contactName || item.contactName || item.ContactName,
        cc: item.connectionData?.CC || item.connectionData?.cc || item.cc || item.CC,
        mobile: item.connectionData?.Mobile || item.connectionData?.mobile || item.mobile || item.Mobile,
        email: item.connectionData?.Email || item.connectionData?.email || item.email || item.Email,
        ConnType: item.connectionData?.ConnType || item.connectionData?.connType || item.connType
      },
      feedbackNotes: item.FdbkNotes || item.feedbackNotes,
      feedbackScore: item.FdbkScore || item.feedbackScore,
      failureReason: item.FailureReason || item.failureReason,
      statusHist: item.StatusHist || item.statusHist,
      customFields: (item?.ExtraFields && item.ExtraFields !== "[]") ? (typeof item.ExtraFields === 'string' ? JSON.parse(item.ExtraFields) : item.ExtraFields) : (item?.CustomFields && item.CustomFields !== "[]" ? (typeof item.CustomFields === 'string' ? JSON.parse(item.CustomFields) : item.CustomFields) : (item?.customFields || []))
    };
  };

  // Order custom status states
  const [statusText, setStatusText] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [byEmail, setByEmail] = useState(false);
  const [byWhatsApp, setByWhatsApp] = useState(false);

  // Extract latest status and notes from StatusHist or threadData if available
  useEffect(() => {
    try {
      const rawHist = currentThreadData?.statusHist || currentThreadData?.StatusHist;
      if (rawHist) {
        const hist = typeof rawHist === 'string'
          ? JSON.parse(rawHist)
          : rawHist;
        if (Array.isArray(hist) && hist.length > 0) {
          const last = hist[hist.length - 1];
          setStatusText(last?.S || last?.Status || currentThreadData?.currStage || currentThreadData?.status || currentThreadData?.Status || '');
          setStatusNotes(last?.N || last?.Notes || last?.D || currentThreadData?.notes || '');
          return;
        }
      }
    } catch (e) {
      console.error('Error parsing StatusHist:', e);
    }
    // If no StatusHist or empty array, fallback to currStage / status / Notes
    setStatusText(currentThreadData?.currStage || currentThreadData?.status || currentThreadData?.Status || '');
    setStatusNotes(currentThreadData?.notes || '');
    setByEmail(false);
    setByWhatsApp(false);
  }, [currentThreadData]);

  // Transform and set thread data
  useEffect(() => {
    const initialStatus = Number(initialData?.StatusID ?? initialData?.statusID ?? initialData?.Sts ?? 0);
    const isInitialCompleted = initialStatus === 2 || initialStatus === 3;
    const sourceData = (isHistoryview || isInitialCompleted) && initialData ? initialData : (newThreadData || initialData);
    if (sourceData) {
      const threadData = getTransformedThreadData(sourceData);
      setCurrentThreadData(threadData);
    }
  }, [newThreadData, initialData, isHistoryview, users, threadStages]);

  // --- Handlers (remain the same) ---
  const handleReassign = () => {
    setDialogStates((prev) => ({ ...prev, reassign: true }));
  };

  const handleReassignConfirm = async (newPOCUserID) => {
    try {
      dispatch(setGlobalLoading(true));
      const result = await reassignThreadAPI(currentThreadData, newPOCUserID);

      if (result.error === false && result.message === 'Success') {
        const newAssignedUser = users.find(u => u.userID === parseInt(newPOCUserID));
        toast.success(`${getThreadNameFromTypeID(currentThreadData?.typeID, threadTypesData, "SnSng")} reassigned to ${newAssignedUser?.name || 'selected user'}.`);
        onThreadUpdated();
      }
    } catch (error) {
      console.error('Error reassigning thread:', error);
      toast.error(`Failed to reassign ${getThreadNameFromTypeID(currentThreadData?.typeID, threadTypesData, "SnSng")}`);
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const handleCustomStatusUpdate = async () => {
    const trimmed = statusText.trim();
    if (!trimmed) {
      toast.error('Please enter or select a status.');
      return;
    }
    try {
      dispatch(setGlobalLoading(true));
      const shouldShowStatus = Boolean(byEmail || byWhatsApp);
      const thrdApiData = {
        ...currentThreadData,
        changeType: 'STATUS',
        newStatusText: trimmed,
        showStatus: shouldShowStatus,
        doneNotes: statusNotes.trim(),
        shareByEmail: byEmail || false,
        shareByWhatsApp: byWhatsApp || false,
        currencySymbol: currencySymbol
      };
      const result = await performThreadActionAPI(thrdApiData, 'STATUS');

      if (result?.error === false && result?.message === 'Success') {
        toast.success('Status updated.');

        if (byWhatsApp) {
          const conn = currentThreadData?.connectionData;
          const phone = (conn?.mobile || '').replace(/\D/g, '');
          const countryCode = (conn?.cc ? String(conn.cc) : '91').replace(/\D/g, '');
          const orderNo = currentThreadData?.reference ? `${currentThreadData.reference}` : (currentThreadData?.threadID || '');
          const bizName = conn?.bizName || 'Customer';
          const trackingLink = `https://sahi.club/web/order?aid=${currentThreadData?.agencyID}&tid=${currentThreadData?.threadID}`;

          const message = `The status of your order *${orderNo}* to *${bizName}* has been updated to *${trimmed}*. \nYou can track the status at : ${trackingLink}`;
          const whatsappUrl = `https://api.whatsapp.com/send?phone=${countryCode}${phone}&text=${encodeURIComponent(message)}`;
          window.open(whatsappUrl, '_blank');
        }

        setStatusNotes('');
        setByEmail(false);
        setByWhatsApp(false);
        onThreadUpdated();
      } else {
        toast.error(result?.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update status');
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const handleUpdateStatus = async (stageID, markAsDone = false, shareByEmail = false, takeFeedback = false, completionNote = '', shareByWhatsApp = false) => {
    try {
      dispatch(setGlobalLoading(true));
      const thrdApiData = {
        ...currentThreadData,
        shareByEmail,
        shareByWhatsApp,
        takeFeedback,
        doneNotes: completionNote,
        currencySymbol: currencySymbol
      };
      const result = markAsDone
        ? await performThreadActionAPI(thrdApiData, 'DONE')
        : await updateThreadStageAPI(thrdApiData, stageID, markAsDone);

      if (result.error === false && result.message === 'Success') {
        toast.success(markAsDone
          ? `${getThreadNameFromTypeID(currentThreadData?.typeID, threadTypesData, "SnSng")} marked as done.`
          : `${getThreadNameFromTypeID(currentThreadData?.typeID, threadTypesData, "SnSng")} status updated.`
        );
        onThreadUpdated();
        if (markAsDone) {
          const isSale = Number(currentThreadData?.typeID) === 2 || Number(currentThreadData?.typeID) === 5;
          if (!isSale) {
            onClose();
          }
        }
      }
    } catch (error) {
      console.error('Error updating thread status:', error);
      toast.error('Failed to update thread status');
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const handleCloseThread = async (rejReason, isMarkCold = false) => {
    try {
      dispatch(setGlobalLoading(true));
      const payloadData = {
        ...currentThreadData,
        doneNotes: rejReason ? rejReason : 'Thread Cancelled',
        isCold: isMarkCold
      };
      const result = await performThreadActionAPI(payloadData, 'CANCEL');

      if (result.error === false) {
        toast.success(`${getThreadNameFromTypeID(currentThreadData?.typeID, threadTypesData, "SnSng")} ${currentThreadData.typeID === 4 ? 'marked as fake' :
          currentThreadData.typeID === 1 ? 'rejected' : 'cancelled'
          }.`);
      }
    } catch (error) {
      console.error('Error while cancelling thread', error);
    } finally {
      dispatch(setGlobalLoading(false));
      onClose();
    }
  };

  const handleConvertInquiry = (inquiryData, action = 'direct') => {
    if (action === 'showDialog') {
      setDialogStates(prev => ({ ...prev, inquiryClose: true }));
    } else if (action === 'converted') {
      setDialogStates(prev => ({ ...prev, inquiryClose: false, inquiryConverted: true }));
    } else if (action === 'ordDone') {
      setDialogStates(prev => ({ ...prev, convertToSales: true, thrEntryCallFrom: 'THRDONE' }));
    } else if (action === 'convert') {
      setDialogStates(prev => ({ ...prev, convertToSales: true, thrEntryCallFrom: 'INQDONE' }));
    } else {
      setDialogStates(prev => ({ ...prev, convertToSales: true }));
    }
  };

  const handleInquiryCloseConfirm = async (action, rejectionReason = null, createOrder = false) => {
    if (action === 'converted') {
      if (createOrder) {
        try {
          dispatch(setGlobalLoading(true));
          const result = await performThreadActionAPI({
            ...currentThreadData,
            doneNotes: 'Converted to Sale'
          }, 'DONE');

          if (result.error === false && result.message === 'Success') {
            // playOrderSound();
            toast.success('Inquiry converted successfully.');
            onClose();
            setDialogStates(prev => ({ ...prev, inquiryClose: false, convertToSales: true }));
          } else {
            toast.error('Failed to convert inquiry');
          }
        } catch (error) {
          console.error('Error converting inquiry:', error);
          toast.error('Failed to convert inquiry');
        } finally {
          dispatch(setGlobalLoading(false));
        }
      } else {
        // playOrderSound();
        handleUpdateStatus(5, true, false, false);
        setDialogStates(prev => ({ ...prev, inquiryClose: false }));
        onClose();
      }
    } else if (action === 'rejected') {
      const selectedReasonText = rejectionReason
        ? rejectionReasonOptions.find(option => option.value === rejectionReason)?.label || rejectionReason
        : rejectionReason;
      handleCloseThread(selectedReasonText, false);
    }
  };

  const handleMarkFake = async (threadData, reasonText = '', isMarkCold = false) => {
    try {
      dispatch(setGlobalLoading(true));
      const threadName = getThreadNameFromTypeID(threadData?.typeID, threadTypesData, "SnSng");
      const isInquiry = threadData?.typeID === 1 || threadData?.typeID === 4;
      const finalReasonText = reasonText || `Marked as Fake ${threadName}`;

      const result = await performThreadActionAPI({
        ...threadData,
        doneNotes: finalReasonText,
        isCold: isMarkCold
      }, 'CANCEL');

      if (result.error === false) {
        if (isInquiry) {
          toast.success(`${threadName} marked as fake.`);
        } else {
          toast.success(`${threadName} cancelled.`);
        }
      }
    } catch (error) {
      console.error(`Error while cancelling ${getThreadNameFromTypeID(threadData?.typeID, threadTypesData, "SnSng")}`, error);
      toast.error(`Failed to cancel ${getThreadNameFromTypeID(threadData?.typeID, threadTypesData, "SnSng")}`);
    } finally {
      dispatch(setGlobalLoading(false));
      onClose();
    }
  };

  const handleAddReasonClick = (type) => {
    if (type === 'disqlReasons' || type === 'rejectionReasons') {
      setMastersDialog({
        open: true,
        masterType: type,
        directAdd: true,
        openEntry: true,
        currentThreadTypeID: currentThreadData?.typeID
      });
    }
  };

  const isCompletedSale = (Number(currentThreadData?.typeID) === 2 || Number(currentThreadData?.typeID) === 5) && Number(currentThreadData?.statusID) === 2;
  const isHistoryOrClosed = isHistoryview || Number(currentThreadData?.statusID) === 2 || Number(currentThreadData?.statusID) === 3;
  const hasContactMobile = Boolean(currentThreadData?.connectionData?.mobile);

  const handleFdbkRequest = async () => {
    if (!currentThreadData) return;
    dispatch(setGlobalLoading(true));
    try {
      const threadData = {
        ...currentThreadData,
        threadID: currentThreadData.threadID || currentThreadData.ThreadID || currentThreadData.actionID,
        typeID: currentThreadData.typeID || currentThreadData.TypeID || 2,
        pocUserID: currentThreadData.pocUserID || currentThreadData.POCUserID || 0,
        shareByWhatsApp: true,
        shareByEmail: false,
        takeFeedback: true,
        connID: currentThreadData.connID || currentThreadData.ConnID,
        reference: currentThreadData.reference || currentThreadData.Reference,
        product: currentThreadData.mainProduct || currentThreadData.MainProduct || currentThreadData.product,
        connectionData: {
          bizName: currentThreadData.connectionData?.bizName || currentThreadData.connectionData?.BizName,
          contactName: currentThreadData.connectionData?.contactName || currentThreadData.connectionData?.ContactName,
          email: currentThreadData.connectionData?.email || currentThreadData.connectionData?.Email,
          mobile: currentThreadData.connectionData?.mobile || currentThreadData.connectionData?.Mobile,
          cc: currentThreadData.connectionData?.cc || currentThreadData.connectionData?.CC
        }
      };

      const result = await performThreadActionAPI(threadData, 'FDBKREQ');
      if (result.error === false) {
        toast.success("Feedback request sent on WhatsApp.");
      } else {
        toast.error(result.message || "Failed to send feedback request.");
      }
    } catch (err) {
      console.error('Error while sending feedback request', err);
      toast.error("Failed to send feedback request.");
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const handleUploadAttachment = async (file) => {
    if (!file || !currentThreadData) return false;

    if (file.size > 1024 * 1024) {
      toast.error("File size must be up to 1 MB only.");
      return false;
    }

    try {
      dispatch(setGlobalLoading(true));
      const uploadResult = await apiService.uploadFile(file, "Threads", "both");

      if (uploadResult?.error) {
        toast.error(uploadResult?.message || "Failed to upload file");
        return false;
      }

      const uploadedFileName = uploadResult?.data?.relativePath;
      if (!uploadedFileName) {
        toast.error("Failed to get uploaded file path");
        return false;
      }

      const updatedData = {
        ...currentThreadData,
        thrFile: uploadedFileName
      };

      const result = await manageThreadAPI(updatedData, 'EDIT', masterData.threadTypes);

      if (result?.error === false && result?.message === 'Success') {
        toast.success("Document uploaded successfully.");
        setCurrentThreadData(prev => ({
          ...prev,
          thrFile: uploadedFileName
        }));
        onThreadUpdated();
        onClose();
        return true;
      } else {
        toast.error(result?.message || "Failed to save attachment to sale record.");
        return false;
      }
    } catch (err) {
      console.error("Error uploading attachment for completed sale:", err);
      toast.error("Failed to upload document.");
      return false;
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const handleClose = () => {
    onClose();
  };
  // --- End Handlers ---

  if (!isOpen || !currentThreadData) return null;

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={onClose}
        preventOutsideClick={true}
      >
        <DialogContent
          className="h-full sm:h-auto sm:max-h-[95vh] w-full sm:max-w-[700px] flex flex-col"
          style={{ backgroundColor: 'var(--background)' }}
        >
          <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
            <div className="sm:hidden flex items-center justify-between w-full h-8">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                  <ArrowLeft className="h-6 w-6" />
                  <span className="sr-only">Back</span>
                </DialogClose>
                <DialogTitle className="text-lg font-medium">
                  <div className="truncate">
                    {threadTypeMeta?.nameSng || 'Thread'}{currentThreadData?.reference ? ` - #${currentThreadData.reference}` : ''}
                  </div>
                </DialogTitle>
              </div>
              <div className="flex items-center gap-1">
                {isCompletedSale && hasContactMobile && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleFdbkRequest}
                    title="Send feedback request on WhatsApp"
                    className="h-9 w-9 p-0 hover:bg-sahi-teal/10 text-sahi-teal flex-shrink-0"
                  >
                    <MessageSquareText className="h-4 w-4" />
                  </Button>
                )}
                {canShowEditButton && ((currentThreadData?.currStageID !== 7 && !isHistoryview) || isCompletedSale) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDialogStates((prev) => ({ ...prev, threadEntry: true }))}
                    className="h-9 w-9 p-0 hover:bg-sahi-blue-light flex-shrink-0"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <DialogTitle className="hidden sm:block text-lg md:text-xl">
              {threadTypeMeta?.nameSng || 'Thread'}{currentThreadData?.reference ? ` - #${currentThreadData.reference}` : ''} - {currentThreadData?.connectionData?.bizName || currentThreadData?.connectionData?.contactName || ""}
            </DialogTitle>
            <div className="hidden sm:flex items-center gap-2">
              {isCompletedSale && hasContactMobile && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleFdbkRequest}
                        className="h-9 w-9 p-0 text-sahi-teal hover:bg-sahi-teal/10"
                      >
                        <MessageSquareText className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Send feedback request on WhatsApp</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {canShowEditButton && ((currentThreadData?.currStageID !== 7 && !isHistoryview) || isCompletedSale) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDialogStates((prev) => ({ ...prev, threadEntry: true }))}
                  className="h-9 w-9 p-0 sm:text-text-color sm:hover:bg-gray-200"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              <DialogClose className="flex-shrink-0" tabIndex={-1}>
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>
          </DialogHeader>

          {/* Main Content - Scrollable */}
          <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-4 w-full">
            {/* Thread Details */}
            <ThrDetailsInfo
              threadData={currentThreadData}
              threadTypeMeta={threadTypeMeta}
              canShowAssignedTo={canShowReassignButton}
              canShowReassignButton={canShowReassignButton && !isHistoryOrClosed}
              onReassign={handleReassign}
            />

            {/* Sentiment / Feedback Section */}
            <FeedbackSection
              feedbackScore={currentThreadData?.feedbackScore}
              feedbackNotes={currentThreadData?.feedbackNotes}
              failureReason={currentThreadData?.failureReason}
            />

            {/* Status Section - Hide for history or completed/closed sales */}
            {!isHistoryOrClosed && (
              <ThrDetailsStatus
                threadData={currentThreadData}
                threadTypeMeta={threadTypeMeta}
                onStageUpdate={handleUpdateStatus}
                isOrder={currentThreadData?.typeID === 2}
                connectionData={currentThreadData?.connectionData}
                statusText={statusText}
                setStatusText={setStatusText}
                notes={statusNotes}
                setNotes={setStatusNotes}
                byEmail={byEmail}
                setByEmail={setByEmail}
                byWhatsApp={byWhatsApp}
                setByWhatsApp={setByWhatsApp}
              />
            )}

            {/* Desktop: Actions Section at Bottom */}
            {isHistoryOrClosed ? (
              <div className="pt-2">
                <Button
                  type="button"
                  onClick={handleClose}
                  className="sahi-secondary-button text-sm px-3.5 py-2 h-auto rounded-[5px] border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                >
                  <X className="h-4 w-4 mr-1" />
                  Close
                </Button>
              </div>
            ) : (
              <ThrDetailsActions
                threadData={currentThreadData}
                onReassign={handleReassign}
                onUpdateStatus={handleUpdateStatus}
                onCloseThread={handleCloseThread}
                onConvertInquiry={handleConvertInquiry}
                onMarkFake={handleMarkFake}
                canShowReassignButton={canShowReassignButton}
                onCloseModal={handleClose}
                isOrder={currentThreadData?.typeID === 2}
                onCustomStatusUpdate={handleCustomStatusUpdate}
                statusText={statusText}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Internal Dialogs (remain outside the main Dialog) */}
      <ReassignDialog
        open={dialogStates.reassign}
        onOpenChange={(open) => setDialogStates(prev => ({ ...prev, reassign: open }))}
        threadData={currentThreadData}
        onReassign={handleReassignConfirm}
      />

      <InquiryCloseDialog
        open={dialogStates.inquiryClose}
        onOpenChange={(open) => setDialogStates(prev => ({ ...prev, inquiryClose: open }))}
        onConfirm={handleInquiryCloseConfirm}
        rejectionReasonOptions={rejectionReasonOptions}
      />

      <CancelThreadDialog
        open={dialogStates.rejectionDialog}
        onOpenChange={(open) => setDialogStates(prev => ({ ...prev, rejectionDialog: open }))}
        onConfirm={handleCloseThread}
        rejectionReasonOptions={rejectionReasonOptions}
        requireReason={true}
        typeID={currentThreadData?.typeID}
        onAddReasonClick={handleAddReasonClick}
        connID={currentThreadData?.connID}
      />

      <ThreadEntry
        triggerButton={false}
        open={dialogStates.convertToSales}
        onOpenChange={(open) => setDialogStates(prev => ({ ...prev, convertToSales: open }))}
        threadTypeID={2}
        preselectedConnID={currentThreadData?.connID}
        preselectedContactPerson={currentThreadData?.connectionData?.contactName}
        onThreadUpdated={() => {
          onClose();
        }}
        prefilledData={{
          connID: currentThreadData?.connID,
          contactPerson: currentThreadData?.connectionData?.contactName || currentThreadData?.contactPerson,
          product: currentThreadData?.product || currentThreadData?.mainProduct,
          model: currentThreadData?.model,
          reference: currentThreadData?.reference,
          quantity: currentThreadData?.quantity || currentThreadData?.mainQty,
          businessValue: currentThreadData?.businessValue || currentThreadData?.bizProspect,
          notes: currentThreadData?.notes,
          source: currentThreadData?.source,
          inquiryThreadID: currentThreadData?.threadID,
          pocUserID: currentThreadData?.pocUserID,
          ...(currentThreadData?.sinceDT && { sinceDT: currentThreadData.sinceDT }),
        }}
        calledFrom={dialogStates.thrEntryCallFrom}
      />

      <ThreadEntry
        triggerButton={false}
        open={dialogStates.threadEntry}
        onOpenChange={(open) => setDialogStates(prev => ({ ...prev, threadEntry: open }))}
        threadID={currentThreadData?.threadID}
        preselectedConnID={currentThreadData?.connID}
        threadTypeID={currentThreadData?.typeID}
        prefilledData={{
          connID: currentThreadData?.connID,
          contactPerson: currentThreadData?.connectionData?.contactName || currentThreadData?.contactPerson,
          product: currentThreadData?.product || currentThreadData?.mainProduct,
          model: currentThreadData?.model,
          reference: currentThreadData?.reference,
          quantity: currentThreadData?.quantity || currentThreadData?.mainQty,
          businessValue: currentThreadData?.businessValue || currentThreadData?.bizProspect,
          notes: currentThreadData?.notes,
          source: currentThreadData?.source,
          inquiryThreadID: currentThreadData?.threadID,
          pocUserID: currentThreadData?.pocUserID,
          statusID: currentThreadData?.statusID,
          currStageID: currentThreadData?.currStageID,
          currStage: currentThreadData?.currStage,
          dueDT: currentThreadData?.dueDT,
          sinceDT: currentThreadData?.sinceDT,
          thrFile: currentThreadData?.thrFile,
          customFields: currentThreadData?.customFields
        }}
        onThreadUpdated={() => {
          onThreadUpdated();
        }}
        onThreadDeleted={() => onClose()}
      />

      <MastersDialog
        open={mastersDialog.open}
        onClose={() => setMastersDialog({
          open: false,
          masterType: '',
          directAdd: false,
          openEntry: false,
          showThreadTypeSelection: false,
          currentThreadTypeID: null
        })}
        masterType={mastersDialog.masterType}
        parentDialogOpen={dialogStates.rejectionDialog}
        directAdd={mastersDialog.directAdd}
        openEntry={mastersDialog.openEntry}
        showThreadTypeSelection={mastersDialog.showThreadTypeSelection}
        preselectedThreadTypeID={mastersDialog.currentThreadTypeID}
      />

      {/* Hidden file input for attachment upload */}
      <input
        type="file"
        ref={attachFileInputRef}
        className="hidden"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt,.csv"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) {
            const success = await handleUploadAttachment(file);
            if (!success) {
              setAttachDocDialog(true);
            }
          }
          e.target.value = '';
        }}
      />

      {/* Confirmation AlertDialog for Accept / Attach Document */}
      <AlertDialog
        open={attachDocDialog}
        onOpenChange={(open) => {
          if (!open) {
            setAttachDocDialog(false);
            onClose();
          }
        }}
      >
        <AlertDialogContent className="w-[92%] sm:w-full max-w-md rounded-2xl p-4 bg-white gap-4 border border-zinc-200/80 shadow-2xl">
          <AlertDialogHeader className="text-left gap-1.5">
            <AlertDialogTitle className="text-base sm:text-lg font-semibold text-zinc-900">
              Attach Document / Invoice
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-zinc-600 leading-normal">
              Do you want to attach a document or invoice for this sale?
              {/* <span className="font-semibold text-zinc-900">
                "{currentThreadData?.connectionData?.bizName || currentThreadData?.connectionData?.contactName || currentThreadData?.mainProduct || (currentThreadData?.reference ? `Sale #${currentThreadData.reference}` : "this sale")}"
              </span> */}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="!flex !flex-row sm:!justify-start gap-2.5 pt-1">
            <Button
              onClick={() => {
                attachFileInputRef.current?.click();
              }}
              className="sahi-primary-button flex-1 sm:flex-none sm:min-w-[100px] h-10"
            >
              <Check className="w-3.5 h-3.5 stroke-[2.5]" /> Accept
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setAttachDocDialog(false);
                onClose();
              }}
              className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px] h-10"
            >
              <X className="w-3.5 h-3.5" /> Cancel
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ThrDetails;